import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaCreditCard, FaSearch } from "react-icons/fa";
import { apihubGet } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";

function normalizePaymentOrder(row) {
  if (!row || typeof row !== "object") return null;
  return {
    _id: row._id || row.id,
    reference_id: row.reference_id || row.orderRef || row.order_ref || "",
    waid: row.waid || row.wa_id || row.waId || "",
    total_amount: row.total_amount ?? row.amount ?? null,
    currency: row.currency || "INR",
    gateway: row.gateway || "",
    status: row.status || "",
    createdAt: row.createdAt || row.created_at || null,
  };
}

function normalizePaymentOrdersResponse(res) {
  const data = Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res)
      ? res
      : [];
  return {
    orders: data.map(normalizePaymentOrder).filter(Boolean),
    paging: res?.paging ?? { cursors: { after: null, before: null } },
  };
}

const formatAmount = (amount, currency = "INR") => {
  if (amount === null || amount === undefined || amount === "") return "—";
  if (currency === "INR") return `₹ ${amount}`;
  if (currency === "USD") return `$ ${amount}`;
  return `${amount} ${currency}`;
};

const statusBadge = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "success" || s === "paid" || s === "completed") {
    return <span className="biz-status biz-status-success">Paid</span>;
  }
  if (s === "captured") {
    return <span className="biz-status biz-status-info">Captured</span>;
  }
  if (s === "failed") {
    return <span className="biz-status biz-status-error">Failed</span>;
  }
  return <span className="biz-status">{status || "—"}</span>;
};

const Payments = () => {
  const { projectId } = useProjectPhone();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [cursorAfter, setCursorAfter] = useState(null);
  const [cursorBefore, setCursorBefore] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchOrders = useCallback(
    async ({ cursor, direction, isInitial = false } = {}) => {
      if (!projectId) return;
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({ projectId });
        if (isInitial) {
          params.set("type", "initial");
        } else if (cursor && direction) {
          params.set(direction, cursor);
        } else {
          throw new Error("Invalid pagination state");
        }
        const res = await apihubGet(
          `/getInteractedCustomerDetail/getpaymentorders?${params}`,
        );
        const { orders, paging } = normalizePaymentOrdersResponse(res);
        setOrders(orders);
        setCursorAfter(paging?.cursors?.after || null);
        setCursorBefore(paging?.cursors?.before || null);
      } catch (err) {
        setError(err.message || "Failed to fetch payment orders");
        setOrders([]);
        setCursorAfter(null);
        setCursorBefore(null);
      } finally {
        setLoading(false);
      }
    },
    [projectId],
  );

  useEffect(() => {
    fetchOrders({ isInitial: true });
  }, [fetchOrders]);

  const filtered = useMemo(() => {
    if (!debouncedSearch) return orders;
    return orders.filter((row) => {
      const ref = row.reference_id?.toLowerCase() || "";
      const waid = row.waid || "";
      const status = row.status?.toLowerCase() || "";
      const currency = row.currency?.toLowerCase() || "";
      return (
        ref.includes(debouncedSearch) ||
        waid.includes(debouncedSearch) ||
        status.includes(debouncedSearch) ||
        currency.includes(debouncedSearch)
      );
    });
  }, [orders, debouncedSearch]);

  return (
    <div className="biz-panel-page biz-payments-page">
      <div className="biz-panel-header biz-panel-header-col">
        <h1>
          <FaCreditCard />
          WhatsApp Payment Orders
        </h1>
        <p className="biz-panel-subtitle">
          Track payment links generated through WhatsApp — reference ID, status,
          and transaction history.
        </p>
      </div>

      <div className="biz-payments-toolbar">
        <div className="biz-search-wrap">
          <FaSearch />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by reference, WAID or status"
          />
        </div>
      </div>

      {error && <p className="biz-banner biz-banner-error">{error}</p>}

      <div className="biz-table-wrap">
        {loading ? (
          <p className="biz-table-message">Loading orders…</p>
        ) : (
          <table className="biz-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Reference ID</th>
                <th>WA ID</th>
                <th>Amount</th>
                <th>Gateway</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="biz-table-empty">
                    No orders found
                  </td>
                </tr>
              ) : (
                filtered.map((row, idx) => (
                  <tr key={row._id || idx}>
                    <td>{idx + 1}</td>
                    <td className="biz-table-mono">{row.reference_id || "—"}</td>
                    <td>{row.waid || "—"}</td>
                    <td>{formatAmount(row.total_amount, row.currency)}</td>
                    <td className="biz-capitalize">{row.gateway || "—"}</td>
                    <td>{statusBadge(row.status)}</td>
                    <td>
                      {row.createdAt
                        ? new Date(row.createdAt).toLocaleString()
                        : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="biz-pagination">
        <button
          type="button"
          disabled={!cursorBefore || loading}
          onClick={() => fetchOrders({ cursor: cursorBefore, direction: "before" })}
        >
          Previous
        </button>
        <button
          type="button"
          disabled={!cursorAfter || loading}
          onClick={() => fetchOrders({ cursor: cursorAfter, direction: "after" })}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Payments;
